import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNotifications } from '../context/NotificationContext';
import './NotificationBell.css';

const NotificationBell = () => {
  const {
    notifications,
    unreadCount,
    markAsRead,
    markAllAsRead,
    removeNotification,
    clearAllNotifications
  } = useNotifications();
  
  const [isOpen, setIsOpen] = useState(false);
  const [bellPosition, setBellPosition] = useState({ top: 80, right: 10 });
  const dropdownRef = useRef(null);
  const bellRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        !bellRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleDropdown = () => {
    if (!isOpen && bellRef.current) {
      // Use requestAnimationFrame to batch DOM reads and prevent forced reflow
      requestAnimationFrame(() => {
        if (bellRef.current) {
          const rect = bellRef.current.getBoundingClientRect();
          setBellPosition({
            top: rect.bottom + 10,
            right: window.innerWidth - rect.right
          });
        }
      });
    }
    
    setIsOpen(!isOpen);
    if (!isOpen && unreadCount > 0) {
      markAllAsRead();
    }
  };

  const formatTimestamp = (timestamp) => {
    const now = new Date();
    const notificationTime = new Date(timestamp);
    const diffMs = now - notificationTime;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  const getNotificationByCategoryIcon = (category) => {
    const categoryIcons = {
      shop: '🛒',
      computer: '🖥️',
      attack: '⚔️',
      defense: '🛡️',
      hacking: '💻',
      mining: '⛏️'
    };
    return categoryIcons[category] || '📬';
  };

  return (
    <div className="notification-bell-container">
      <button
        ref={bellRef}
        className={`notification-bell ${unreadCount > 0 ? 'has-unread' : ''}`}
        onClick={toggleDropdown}
        aria-label={`Notifications (${unreadCount} unread)`}
      >
        <span className="bell-icon">🔔</span>
        {unreadCount > 0 && (
          <span className="notification-badge">{unreadCount > 99 ? '99+' : unreadCount}</span>
        )}
      </button>

      
      {isOpen && createPortal(
        <div 
          ref={dropdownRef} 
          className="notification-dropdown"
          style={{
            position: 'fixed',
            top: `${bellPosition.top}px`,
            right: `${bellPosition.right}px`,
            zIndex: 2147483647
          }}
        >
          <div className="notification-header">
            <h3>📬 Notifications</h3>
            <div className="notification-actions">
              {notifications.length > 0 && (
                <button
                  className="clear-all-btn"
                  onClick={clearAllNotifications}
                  title="Clear all notifications"
                >
                  🗑️
                </button>
              )}
            </div>
          </div>

          <div className="notification-list">
            {notifications.length === 0 ? (
              <div className="no-notifications">
                <span className="no-notifications-icon">📭</span>
                <p>No notifications yet</p>
                <small>Your activity will appear here</small>
              </div>
            ) : (
              notifications.map((notification) => (
                <div
                  key={notification.id}
                  className={`notification-item ${notification.read ? 'read' : 'unread'}`}
                  onClick={() => !notification.read && markAsRead(notification.id)}
                >
                  <div className="notification-content">
                    <div className="notification-main">
                      <div className="notification-icon-title">
                        <span className="notification-icon">
                          {notification.icon || getNotificationByCategoryIcon(notification.category)}
                        </span>
                        <div className="notification-text">
                          <h4 className="notification-title">{notification.title}</h4>
                          <p className="notification-message">{notification.message}</p>
                          {notification.details && (
                            <small className="notification-details">{notification.details}</small>
                          )}
                        </div>
                      </div>
                      <div className="notification-meta">
                        <span className="notification-time">
                          {formatTimestamp(notification.timestamp)}
                        </span>
                        <button
                          className="remove-notification-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            removeNotification(notification.id);
                          }}
                          title="Remove notification"
                        >
                          ×
                        </button>
                      </div>
                    </div>
                    {!notification.read && <div className="unread-indicator"></div>}
                  </div>
                </div>
              ))
            )}
          </div>

          {notifications.length > 0 && (
            <div className="notification-footer">
              <small>Showing last {notifications.length} notifications</small>
            </div>
          )}
        </div>,
        document.body
      )}
    </div>
  );
};

export default NotificationBell;